import classNames from 'classnames'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'

import { MultiCopyItem } from './multi-copy-item'
import styles from './multi-copy-section.module.scss'
import { asColorLabel } from '@app-types/color'
import type { HeadingTag } from '@components/heading'
import { Column, Container, Flex, Row } from '@components/layout/grid'
import { Markdown } from '@components/markdown'
import type { MultiCopyItemFragment } from '@graphql/fragments/MultiCopyItemFragment.graphql'
import type { MultiCopySectionFragment } from '@graphql/fragments/MultiCopySectionFragment.graphql'
import { getColSpan, getColSpanByRow } from '@lib/utils/col-span'
import { stripNonAlphaNumeric } from '@lib/utils/text'

export const MultiCopySection: FunctionComponent<Props> = ({
    heading,
    copy,
    itemsCollection,
    align = 'center',
    spacing = 'default',
    headingTag = 'h3',
    background,
    customLayout,
}) => {
    const centered = align === 'center'

    const headingTypeStyle = centered
        ? { _: 'heading-md', md: 'heading-2xl' }
        : 'heading-sm'

    const { items = [] } = itemsCollection ?? {}

    const rowLayouts = useMemo(() => {
        const defaultColSpan = getColSpan(items.length)

        return customLayout
            ? getColSpanByRow(
                  customLayout?.split(',').map((n) => parseInt(n, 10)),
                  items.length,
              )
            : Array.from({ length: items.length }, () => defaultColSpan)
    }, [customLayout, items.length])

    const maxItemHeadingLength = useMemo(() => {
        return items.reduce((max: number, item: MultiCopyItemFragment) => {
            const itemHeading = stripNonAlphaNumeric(item?.heading ?? '')
            return Math.max(max, itemHeading.length)
        }, 0)
    }, [items])

    return (
        <div
            className={classNames(
                styles[`background-${asColorLabel(background)}`],
            )}
        >
            <Container
                className={classNames(
                    styles['container'],
                    centered && styles['center-text'],
                    styles[`${spacing}-spacing`],
                )}
            >
                {!!heading && (
                    <Flex
                        justify={centered ? 'center' : 'flex-start'}
                        className={classNames(
                            styles['heading-container'],
                            !!copy && styles['heading-container--with-copy'],
                        )}
                    >
                        <Markdown
                            markdown={heading}
                            tag={headingTag}
                            typeStyle={headingTypeStyle}
                        />
                    </Flex>
                )}
                {!!copy && (
                    <div
                        className={classNames(
                            styles['copy-container'],
                            !centered && styles['copy-container--left'],
                            {
                                [styles['has-items']]: items.length > 0,
                            },
                        )}
                    >
                        <Markdown
                            tag='div'
                            typeStyle='body-lg'
                            markdown={copy}
                        />
                    </div>
                )}
                <Row className={styles['item-row']}>
                    {items.map((itemProps, itemIndex) => (
                        <Column
                            key={`${
                                itemProps?.heading ?? itemProps?.subheading
                            }--${itemIndex}`}
                            span={rowLayouts[itemIndex]}
                            className={styles['item-column']}
                        >
                            <MultiCopyItem
                                {...itemProps}
                                centered={centered}
                                maxHeadingLength={maxItemHeadingLength}
                                index={itemIndex}
                            />
                        </Column>
                    ))}
                </Row>
            </Container>
        </div>
    )
}

type Props = Omit<MultiCopySectionFragment, 'sys' | 'type'> & {
    headingTag?: HeadingTag
}

export default MultiCopySection
